import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { sanitizeDbError } from '@/lib/sanitizeError';
import type { Tables } from '@/integrations/supabase/types';

export type Organization = Tables<'organizations'>;
export type Profile = Tables<'hire_profiles'>;
export type UserRole = Tables<'hire_user_roles'>;

// Non-sensitive fields safe for all org members
const ORG_PUBLIC_COLUMNS = 'id,name,industry,logo_url,domain,company_description,company_mission,company_values,company_benefits,company_size,founded_year,headquarters,website_url,social_links,brand_colors,auto_advance_to_interview,auto_send_interview_invite,auto_advance_signed_contracts_to_hired,lifecycle_status,lifecycle_reason,lifecycle_requested_at,lifecycle_processed_at,suspended_at,deactivated_at,deleted_at,anonymized_at,retention_basis,purge_due_at,created_at,updated_at' as const;

export function useOrganization() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select(ORG_PUBLIC_COLUMNS)
        .eq('id', orgId!)
        .single();
      if (error) throw error;
      return data as Organization;
    },
    enabled: !!orgId,
  });
}

/** Fetches sensitive org fields (VAT, CR, contact info) — only returns data for admin/owner roles */
export function useOrgSensitiveFields() {
  const { profile, role } = useAuth();
  const orgId = profile?.org_id;
  const isAdmin = role === 'org_owner' || role === 'org_admin';

  return useQuery({
    queryKey: ['organization-sensitive', orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_org_sensitive_fields', { _org_id: orgId! });
      if (error) throw error;
      return (data && data.length > 0) ? data[0] : null;
    },
    enabled: !!orgId && isAdmin,
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Organization> & { id: string }) => {
      const { error } = await supabase.from('organizations').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organization'] });
      qc.invalidateQueries({ queryKey: ['organization-sensitive'] });
      toast({ title: 'Organization updated' });
    },
    onError: (e) => {
      toast({ title: 'Update failed', description: sanitizeDbError(e), variant: 'destructive' });
    },
  });
}

export function useTeamMembers() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['team', orgId],
    queryFn: async () => {
      const [profilesRes, rolesRes] = await Promise.all([
        supabase.from('hire_profiles').select('*').eq('org_id', orgId!),
        supabase.from('hire_user_roles').select('user_id, role').eq('org_id', orgId!),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (rolesRes.error) throw rolesRes.error;
      return (profilesRes.data || []).map(p => ({
        ...p,
        role: rolesRes.data?.find(r => r.user_id === p.user_id)?.role || 'viewer',
      }));
    },
    enabled: !!orgId,
  });
}
